import { errorMessage } from "@atlas/shared";
import React from "react";
import { useParams, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import {
  MountainSnow,
  Route,
  Sun,
  Globe2,
  Trophy,
  Lock,
  ChevronDown,
  Sparkles,
  CalendarHeart,
} from "lucide-react";
import { fetchWrapped, fetchWrappedShareUrl, toShareCardImageUrl } from "../api/wrapped";
import { isWrappedSeasonOpen } from "../lib/wrapped-season";
import { ApiError } from "../lib/query-client";
import { StatBlock } from "../components/StatBlock";
import { AchievementBadge } from "../components/AchievementBadge";
import { ShareCardButton } from "../components/ShareCardButton";
import { Button } from "../components/Button";
import { EmptyState } from "../components/EmptyState";

// ─── Scoped styles ────────────────────────────────────────────────────────────
// Full-viewport-height story sections with scroll-snap; content within each
// section fades/rises into view via IntersectionObserver. prefers-reduced-motion
// disables both the reveal transition and the scroll-hint bounce, per the
// design system's motion rules (specs/00-overview.md).

const CSS = `
.wr-scroller{ height:100%; overflow-y:auto; scroll-snap-type:y mandatory; }
.wr-section{
  min-height:100%; scroll-snap-align:start; scroll-snap-stop:always;
  display:flex; flex-direction:column; align-items:center; justify-content:center;
  padding:56px 32px; position:relative; text-align:center;
}
[data-wr-reveal]{
  opacity:0; transform:translateY(28px);
  transition:opacity .7s var(--ease-out), transform .7s var(--ease-out);
}
[data-wr-reveal].wr-in{ opacity:1; transform:none; }
@keyframes wr-bounce{ 0%,100%{ transform:translateY(0); } 50%{ transform:translateY(8px); } }
.wr-scroll-hint{ animation:wr-bounce 1.8s ease-in-out infinite; }
@media (prefers-reduced-motion: reduce){
  [data-wr-reveal]{ opacity:1 !important; transform:none !important; transition:none !important; }
  .wr-scroll-hint{ animation:none !important; }
}
`;

function ensureCss() {
  if (typeof document === "undefined") return;
  if (!document.getElementById("atlas-wrapped-css")) {
    const s = document.createElement("style");
    s.id = "atlas-wrapped-css";
    s.textContent = CSS;
    document.head.appendChild(s);
  }
}

function useScrollReveal(containerRef: React.RefObject<HTMLDivElement | null>, ready: boolean) {
  React.useEffect(() => {
    if (!ready) return;
    const container = containerRef.current;
    if (!container) return;
    const reduced =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const els = container.querySelectorAll<HTMLElement>("[data-wr-reveal]");
    if (reduced) {
      els.forEach((el) => el.classList.add("wr-in"));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) e.target.classList.add("wr-in");
          else e.target.classList.remove("wr-in");
        });
      },
      { threshold: 0.4, root: container },
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [containerRef, ready]);
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function MonthlyRhythm({ monthly }: { monthly: number[] }) {
  const max = Math.max(1, ...monthly);
  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: "clamp(2px, 1.5vw, 10px)", height: 180, width: "100%", maxWidth: 560 }}>
      {monthly.map((count, i) => (
        <div
          key={i}
          style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 8, height: "100%", justifyContent: "flex-end" }}
        >
          <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--text-muted)" }} className="tnum">
            {count > 0 ? count : ""}
          </span>
          <div
            style={{
              width: "100%",
              maxWidth: 30,
              height: `${Math.max(4, (count / max) * 130)}px`,
              background: count > 0 ? "linear-gradient(180deg, var(--gold-300), var(--gold-600))" : "var(--surface-raised)",
              borderRadius: "var(--radius-sm) var(--radius-sm) 0 0",
              boxShadow: count > 0 ? "var(--glow-gold-sm)" : "none",
            }}
          />
          <span style={{ fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--text-faint)", textTransform: "uppercase" }}>
            {MONTHS[i]}
          </span>
        </div>
      ))}
    </div>
  );
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
}

// ─── Upgrade panel (free plan hits the 402) ────────────────────────────────────

function WrappedUpgradePanel({ year }: { year: number }) {
  return (
    <div
      className="atlas-topo"
      style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        gap: 16,
        padding: 32,
      }}
    >
      <div
        style={{
          width: 56,
          height: 56,
          borderRadius: "50%",
          display: "grid",
          placeItems: "center",
          background: "var(--accent-soft)",
          border: "1px solid var(--border-gold)",
          color: "var(--gold-400)",
        }}
      >
        <Lock size={22} />
      </div>
      <p className="eyebrow" style={{ fontSize: 11 }}>Atlas · {year} Wrapped</p>
      <h1 style={{ font: "var(--type-h2)", color: "var(--text-primary)", margin: 0, maxWidth: 460 }}>
        Yearly Wrapped is a Pro feature
      </h1>
      <p style={{ fontSize: 14, color: "var(--text-secondary)", maxWidth: 420, lineHeight: 1.6, margin: 0 }}>
        Upgrade to unlock your {year} year in review: distance, summits, biggest day, your
        rarest achievement and a shareable summary card.
      </p>
      <Link to="/plan" style={{ textDecoration: "none", marginTop: 8 }}>
        <Button variant="primary" leftIcon={<Sparkles size={15} />}>
          Upgrade to Pro
        </Button>
      </Link>
    </div>
  );
}

// ─── WrappedPage ────────────────────────────────────────────────────────────────

export default function WrappedPage() {
  ensureCss();
  const { year: yearParam } = useParams({ strict: false }) as { year: string };
  const year = parseInt(yearParam, 10);
  const seasonOpen = isWrappedSeasonOpen();
  const containerRef = React.useRef<HTMLDivElement>(null);

  const wrappedQuery = useQuery({
    queryKey: ["wrapped", year],
    queryFn: () => fetchWrapped(year),
    retry: (failureCount, error) => !(error instanceof ApiError) && failureCount < 2,
    enabled: Number.isInteger(year) && seasonOpen,
  });

  const isLimitReached = wrappedQuery.error instanceof ApiError && wrappedQuery.error.status === 402;
  const wrapped = wrappedQuery.data?.wrapped ?? null;

  const shareUrlQuery = useQuery({
    queryKey: ["wrapped", year, "share-url"],
    queryFn: () => fetchWrappedShareUrl(year),
    enabled: !!wrapped,
    retry: false,
  });

  useScrollReveal(containerRef, !!wrapped);

  if (!Number.isInteger(year)) {
    return (
      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <EmptyState icon={<CalendarHeart size={18} />} title="That year isn't available" />
      </div>
    );
  }

  // Wrapped is seasonal — mid-December to mid-January. Outside the window the
  // entry points are already hidden; this covers direct navigation / bookmarks.
  if (!seasonOpen) {
    return (
      <div
        className="atlas-topo"
        style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: 32 }}
      >
        <EmptyState
          icon={<CalendarHeart size={18} />}
          title="Wrapped opens in December"
          hint="Your year in review unlocks mid-December and stays open until mid-January. Check back at the turn of the year."
        />
      </div>
    );
  }

  if (isLimitReached) {
    return <WrappedUpgradePanel year={year} />;
  }

  if (wrappedQuery.isError) {
    return (
      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <EmptyState
          icon={<CalendarHeart size={18} />}
          title="That year isn't available"
          hint={errorMessage(wrappedQuery.error, "Try again in a moment.")}
        />
      </div>
    );
  }

  if (wrappedQuery.isLoading || !wrapped) {
    return (
      <div
        className="atlas-topo"
        style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: "var(--text-muted)" }}
      >
        <p className="eyebrow">Building your {year} Wrapped…</p>
      </div>
    );
  }

  const shareImageUrl = shareUrlQuery.data ? toShareCardImageUrl(shareUrlQuery.data.url) : null;

  return (
    <div ref={containerRef} className="wr-scroller">
      {/* ── Opener ─────────────────────────────────────────────────────── */}
      <section className="wr-section atlas-topo">
        <div data-wr-reveal>
          <p className="eyebrow" style={{ fontSize: 12, marginBottom: 14 }}>Atlas · {year} Wrapped</p>
          <h1
            style={{
              font: "var(--type-display)",
              fontSize: "clamp(32px, 6vw, 56px)",
              color: "var(--text-primary)",
              margin: "0 0 16px",
              lineHeight: 1.1,
            }}
          >
            Your {year} in the outdoors
          </h1>
          <p style={{ fontSize: 16, color: "var(--text-secondary)", maxWidth: 480, margin: "0 auto" }}>
            {wrapped.activityCount > 0
              ? `${wrapped.activityCount.toLocaleString()} activities, one full year of exploring. Let's look back.`
              : "A quiet year on Atlas — let's see what's on the books."}
          </p>
        </div>
        <div
          className="wr-scroll-hint"
          style={{ position: "absolute", bottom: 32, color: "var(--text-faint)", display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}
        >
          <span className="eyebrow" style={{ fontSize: 10 }}>Scroll</span>
          <ChevronDown size={18} />
        </div>
      </section>

      {/* ── Distance + days out ───────────────────────────────────────── */}
      <section className="wr-section">
        <div data-wr-reveal style={{ display: "flex", flexDirection: "column", gap: 40, alignItems: "center" }}>
          <p className="eyebrow" style={{ fontSize: 12 }}>Ground covered</p>
          <div style={{ display: "flex", gap: 56, flexWrap: "wrap", justifyContent: "center" }}>
            <StatBlock
              size="lg"
              align="center"
              gold
              icon={<Route size={13} />}
              label="Distance"
              value={`${wrapped.totalDistanceKm.toLocaleString(undefined, { maximumFractionDigits: 1 })} km`}
            />
            <StatBlock
              size="lg"
              align="center"
              gold
              icon={<Sun size={13} />}
              label="Days out"
              value={wrapped.activeDays.toLocaleString()}
            />
          </div>
          <p style={{ fontSize: 14, color: "var(--text-muted)", maxWidth: 420 }}>
            Across {wrapped.activityCount.toLocaleString()} logged activities this year.
          </p>
        </div>
      </section>

      {/* ── Summits + biggest day ─────────────────────────────────────── */}
      <section className="wr-section">
        <div data-wr-reveal style={{ display: "flex", flexDirection: "column", gap: 32, alignItems: "center", maxWidth: 520 }}>
          <p className="eyebrow" style={{ fontSize: 12 }}>Summits</p>
          <StatBlock size="lg" align="center" gold icon={<MountainSnow size={13} />} label="Peaks summited" value={wrapped.summits.toLocaleString()} />
          {wrapped.biggestDay ? (
            <div
              style={{
                background: "var(--surface-card)",
                border: "1px solid var(--border-gold)",
                borderRadius: "var(--radius-lg)",
                boxShadow: "var(--glow-gold-sm), var(--ring-top)",
                padding: "18px 24px",
                marginTop: 8,
              }}
            >
              <p className="eyebrow" style={{ fontSize: 10, marginBottom: 6 }}>Biggest day</p>
              <p style={{ font: "var(--type-h3)", color: "var(--text-primary)", margin: "0 0 4px" }}>
                {formatDate(wrapped.biggestDay.date)}
              </p>
              <p style={{ fontSize: 13, color: "var(--text-secondary)", margin: 0 }} className="tnum">
                {wrapped.biggestDay.distanceKm.toLocaleString(undefined, { maximumFractionDigits: 1 })} km
                {wrapped.biggestDay.peaks > 0 ? ` · ${wrapped.biggestDay.peaks} summit${wrapped.biggestDay.peaks === 1 ? "" : "s"}` : ""}
              </p>
            </div>
          ) : (
            <p style={{ fontSize: 14, color: "var(--text-muted)" }}>No activity logged yet in {year}.</p>
          )}
        </div>
      </section>

      {/* ── New regions ────────────────────────────────────────────────── */}
      <section className="wr-section">
        <div data-wr-reveal style={{ display: "flex", flexDirection: "column", gap: 20, alignItems: "center" }}>
          <p className="eyebrow" style={{ fontSize: 12 }}>New ground</p>
          <StatBlock size="lg" align="center" gold icon={<Globe2 size={13} />} label="Regions explored" value={wrapped.newRegions.toLocaleString()} />
          <p style={{ fontSize: 14, color: "var(--text-muted)", maxWidth: 420 }}>
            Plus {wrapped.newLandmarks.toLocaleString()} landmarks visited and{" "}
            {wrapped.trailSectionsCompleted.toLocaleString()} trail sections completed.
          </p>
        </div>
      </section>

      {/* ── Rarest achievement ─────────────────────────────────────────── */}
      <section className="wr-section">
        <div data-wr-reveal style={{ display: "flex", flexDirection: "column", gap: 20, alignItems: "center", width: "100%", maxWidth: 420 }}>
          <p className="eyebrow" style={{ fontSize: 12 }}>Rarest unlock</p>
          {wrapped.rarestAchievement ? (
            <>
              <AchievementBadge
                title={wrapped.rarestAchievement.name}
                tier={wrapped.rarestAchievement.tier as "bronze" | "silver" | "gold" | "platinum" | "mythic"}
                points={wrapped.rarestAchievement.points}
                unlocked
                icon={<Trophy size={20} />}
                className="wr-achievement"
              />
              <p style={{ fontSize: 13, color: "var(--text-muted)", margin: 0 }}>
                Unlocked {formatDate(wrapped.rarestAchievement.unlockedAt)} · held by{" "}
                {wrapped.rarestAchievement.holderCount.toLocaleString()}{" "}
                {wrapped.rarestAchievement.holderCount === 1 ? "explorer" : "explorers"}
              </p>
            </>
          ) : (
            <p style={{ fontSize: 14, color: "var(--text-muted)" }}>No achievements unlocked in {year} yet.</p>
          )}
        </div>
      </section>

      {/* ── Monthly rhythm ─────────────────────────────────────────────── */}
      <section className="wr-section">
        <div data-wr-reveal style={{ display: "flex", flexDirection: "column", gap: 28, alignItems: "center", width: "100%" }}>
          <p className="eyebrow" style={{ fontSize: 12 }}>Your rhythm</p>
          <MonthlyRhythm monthly={wrapped.monthly} />
        </div>
      </section>

      {/* ── Finale ─────────────────────────────────────────────────────── */}
      <section className="wr-section">
        <div data-wr-reveal style={{ display: "flex", flexDirection: "column", gap: 20, alignItems: "center", width: "100%", maxWidth: 480 }}>
          <p className="eyebrow" style={{ fontSize: 12 }}>That's a wrap</p>
          <h2 style={{ font: "var(--type-h2)", color: "var(--text-primary)", margin: "0 0 4px" }}>
            {wrapped.totalDistanceKm.toLocaleString(undefined, { maximumFractionDigits: 0 })} km closer to knowing this land.
          </h2>
          <div
            style={{
              width: "100%",
              borderRadius: "var(--radius-lg)",
              overflow: "hidden",
              border: "1px solid var(--border-subtle)",
              boxShadow: "var(--shadow-md)",
              background: "var(--surface-sunken)",
              aspectRatio: "1200 / 630",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {shareImageUrl ? (
              <img
                src={shareImageUrl}
                alt={`Your ${year} Atlas Wrapped summary card`}
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            ) : (
              <span className="eyebrow" style={{ fontSize: 10 }}>Preparing your card…</span>
            )}
          </div>
          <ShareCardButton fetchUrl={() => fetchWrappedShareUrl(year)} label="Share your Wrapped" size="md" />
        </div>
      </section>
    </div>
  );
}
